import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection } from "lexical";
import { $isListItemNode, ListNode } from "@lexical/list";

/**
 * Plugin to prevent space key from toggling checkboxes in checklist items.
 * Space inserts a normal space character.
 * Ctrl/Cmd+Space toggles the checkbox instead.
 */
export function CheckListKeyboardPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isSpace = event.key === " ";
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      // Only intercept space key (with or without Ctrl/Cmd)
      if (!isSpace) {
        return;
      }

      editor.getEditorState().read(() => {
        const selection = $getSelection();

        // If there's no selection or it's a range selection, allow default behavior
        if (!selection || ($isRangeSelection(selection) && !selection.isCollapsed())) {
          return;
        }

        // Check if we're inside a checklist item
        let isInChecklistItem = false;
        let checklistItem = null;

        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          let currentNode: typeof anchorNode | null = anchorNode;

          // Walk up the tree to check if we're in a list item
          while (currentNode) {
            if ($isListItemNode(currentNode)) {
              // Check if this list item is part of a checklist
              const parentListNode = currentNode.getParent();
              if (parentListNode instanceof ListNode) {
                if (parentListNode.getListType() === "check") {
                  isInChecklistItem = true;
                  checklistItem = currentNode;
                }
              }
              break;
            }
            currentNode = currentNode.getParent();
          }
        }

        // Handle space key in checklist items
        if (isInChecklistItem) {
          event.preventDefault();

          if (isCtrlOrCmd && checklistItem) {
            // Ctrl/Cmd+Space: Toggle the checkbox
            editor.update(() => {
              checklistItem?.toggleChecked();
            });
          } else {
            // Space: Insert space character normally
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                selection.insertText(" ");
              }
            });
          }
        }
      });
    };

    // Get the root element of the editor and add the keydown listener
    const rootElement = editor.getRootElement();
    if (rootElement) {
      rootElement.addEventListener("keydown", handleKeyDown, true);
    }

    return () => {
      const rootElement = editor.getRootElement();
      if (rootElement) {
        rootElement.removeEventListener("keydown", handleKeyDown, true);
      }
    };
  }, [editor]);

  return null;
}
